/**
 * Opted-Out Apps Manager
 *
 * Manages list of ecosystem applications that the user has opted-out of auto-trust.
 * When a user removes an ecosystem app from their trusted list, it is added here
 * to prevent automatic re-addition on future authentications.
 *
 * Data is stored in iframe's localStorage (isolated from parent domain)
 */

export interface OptedOutApp {
  userId: string
  projectId: string
  origin: string
  optedOutAt: number
}

const STORAGE_KEY = 'lumia_passport_opted_out_apps'

export class OptedOutAppsManager {
  /**
   * Check if user has opted out of auto-trust for this app
   */
  isOptedOut(userId: string, projectId: string, origin: string): boolean {
    const optedOutApps = this.getOptedOutApps()
    const result = optedOutApps.some(
      (app) =>
        app.userId === userId &&
        app.projectId === projectId &&
        app.origin === origin
    )
    return result
  }

  /**
   * Add app to opted-out list (when user removes ecosystem app from trusted list)
   */
  addOptedOut(userId: string, projectId: string, origin: string): void {
    const optedOutApps = this.getOptedOutApps()

    // Check if already opted-out
    const exists = optedOutApps.some(
      (app) =>
        app.userId === userId &&
        app.projectId === projectId &&
        app.origin === origin
    )

    if (exists) {
      return
    }

    // Add to opted-out list
    optedOutApps.push({
      userId,
      projectId,
      origin,
      optedOutAt: Date.now(),
    })

    this.saveOptedOutApps(optedOutApps)
  }

  /**
   * Remove app from opted-out list (when user manually re-trusts via checkbox)
   */
  removeOptedOut(userId: string, projectId: string, origin: string): void {
    const optedOutApps = this.getOptedOutApps()
    const filtered = optedOutApps.filter(
      (app) =>
        !(
          app.userId === userId &&
          app.projectId === projectId &&
          app.origin === origin
        )
    )

    this.saveOptedOutApps(filtered)
  }

  /**
   * Clear all opted-out apps for a user (when clearing all trusted apps)
   */
  clearOptedOutForUser(userId: string): void {
    const optedOutApps = this.getOptedOutApps()
    const filtered = optedOutApps.filter((app) => app.userId !== userId)

    this.saveOptedOutApps(filtered)
  }

  /**
   * Get all opted-out apps from storage
   */
  private getOptedOutApps(): OptedOutApp[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      if (!data) {
        return []
      }

      const apps = JSON.parse(data)
      return Array.isArray(apps) ? apps : []
    } catch (error) {
      console.error('[OptedOutApps] Error reading opted-out apps:', error)
      return []
    }
  }

  /**
   * Save opted-out apps to storage
   */
  private saveOptedOutApps(apps: OptedOutApp[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apps))
    } catch (error) {
      // Fail gracefully to avoid breaking auth/remove flows
      console.error('[OptedOutApps] Error saving opted-out apps:', error)
    }
  }
}
